import React, { useState, useEffect, useMemo } from 'react';
import './PermissionsGrid.css';
import permissionsAPI from '../api/permissions';

function PermissionsGrid({ users = [], currentUser, onUpdate }) {
  const [systemFunctions, setSystemFunctions] = useState(null);
  const [usersWithPermissions, setUsersWithPermissions] = useState([]);
  const [permissionsData, setPermissionsData] = useState({}); // { userId: { permissionPath: true/false } }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [selectedUserColumn, setSelectedUserColumn] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [functions, usersData] = await Promise.all([
        permissionsAPI.getFunctions(),
        permissionsAPI.getUsersWithPermissions()
      ]);
      setSystemFunctions(functions);
      
      // Filtruj użytkowników - Owner ma pełny dostęp i nie jest zarządzany przez grid
      const filteredUsersData = usersData.filter(user => user.role !== 'owner');
      setUsersWithPermissions(filteredUsersData);
      
      // Załaduj uprawnienia wszystkich użytkowników (oprócz Owner) do permissionsData
      const permissionsMap = {};
      for (const user of filteredUsersData) {
        // Upewnij się, że permissions jest obiektem (może być stringiem JSON)
        let userPerms = user.permissions || {};
        if (typeof userPerms === 'string') {
          try {
            userPerms = JSON.parse(userPerms);
          } catch (e) {
            console.warn(`Błąd parsowania uprawnień dla użytkownika ${user.id}:`, e);
            userPerms = {};
          }
        }
        // Upewnij się, że userPerms jest obiektem (nie null, nie undefined)
        if (!userPerms || typeof userPerms !== 'object') {
          userPerms = {};
        }
        // WAŻNE: Użyj głębokiej kopii, aby uniknąć mutacji oryginalnych danych
        permissionsMap[user.id] = JSON.parse(JSON.stringify(userPerms));
        console.log(`Załadowano uprawnienia dla użytkownika ${user.id} (${user.name}):`, permissionsMap[user.id]);
      }
      console.log('Wszystkie załadowane uprawnienia:', permissionsMap);
      setPermissionsData(permissionsMap);
    } catch (err) {
      console.error('Błąd ładowania danych:', err);
      setError(err.message || 'Błąd ładowania danych');
    } finally {
      setLoading(false);
    }
  };

  // Funkcja do spłaszczania struktury uprawnień na płaską listę dla gridu
  const flattenPermissions = (functions) => {
    const flatList = [];
    
    const processPermissions = (permissions, basePath, category) => {
      if (!permissions) return;
      
      Object.entries(permissions).forEach(([key, value]) => {
        const currentPath = basePath ? `${basePath}.${key}` : key;
        
        if (value.permissions) {
          // To jest zagnieżdżona sekcja z poduprawnieniami (np. hr.employees, dashboard.toolbar)
          const sectionCategory = value.label || category;
          processPermissions(value.permissions, currentPath, sectionCategory);
        } else if (value.label) {
          // To jest proste uprawnienie
          flatList.push({
            path: currentPath,
            label: value.label,
            description: value.description || '',
            category: category || 'Ogólne',
            fullPath: currentPath
          });
        }
      });
    };

    Object.entries(functions).forEach(([moduleKey, moduleData]) => {
      if (moduleData.permissions) {
        processPermissions(moduleData.permissions, moduleKey, moduleData.label);
      }
    });

    return flatList;
  };

  // Spłaszczona lista uprawnień dla gridu
  const permissionsList = useMemo(() => {
    if (!systemFunctions) return [];
    return flattenPermissions(systemFunctions);
  }, [systemFunctions]);

  // Filtrowanie użytkowników
  const filteredUsers = useMemo(() => {
    if (!searchTerm) return usersWithPermissions;
    const search = searchTerm.toLowerCase();
    return usersWithPermissions.filter(user => 
      user.name?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search) ||
      user.role?.toLowerCase().includes(search)
    );
  }, [usersWithPermissions, searchTerm]);

  // Filtrowanie uprawnień
  const filteredPermissions = useMemo(() => {
    if (!searchTerm) return permissionsList;
    const search = searchTerm.toLowerCase();
    return permissionsList.filter(perm => 
      perm.label.toLowerCase().includes(search) ||
      perm.description.toLowerCase().includes(search) ||
      perm.category.toLowerCase().includes(search)
    );
  }, [permissionsList, searchTerm]);

  // Funkcja do pobrania wartości uprawnienia dla użytkownika
  const getPermissionValue = (userId, permissionPath) => {
    const userPerms = permissionsData[userId] || {};
    
    // Upewnij się, że userPerms jest obiektem
    let perms = userPerms;
    if (typeof perms === 'string') {
      try {
        perms = JSON.parse(perms);
      } catch (e) {
        console.warn(`Błąd parsowania uprawnień dla użytkownika ${userId}:`, e);
        return false;
      }
    }
    
    const parts = permissionPath.split('.');
    let current = perms;
    
    for (const part of parts) {
      if (current && typeof current === 'object' && part in current) {
        current = current[part];
      } else {
        return false;
      }
    }
    
    return current === true;
  };

  // Funkcja do zmiany wartości uprawnienia
  const handlePermissionChange = (userId, permissionPath, value) => {
    setHasUnsavedChanges(true);
    setError('');
    
    const newPermissionsData = { ...permissionsData };
    if (!newPermissionsData[userId]) {
      newPermissionsData[userId] = {};
    }
    
    const parts = permissionPath.split('.');
    let current = newPermissionsData[userId];
    
    // Utwórz zagnieżdżoną strukturę
    for (let i = 0; i < parts.length - 1; i++) {
      if (!current[parts[i]]) {
        current[parts[i]] = {};
      }
      current = current[parts[i]];
    }
    
    current[parts[parts.length - 1]] = value;
    setPermissionsData(newPermissionsData);
  };

  // Funkcja do zapisania wszystkich zmian
  const handleSaveAll = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // Zapisz uprawnienia dla wszystkich użytkowników, którzy mają zmiany
      const saveResults = [];
      for (const [userId, permissions] of Object.entries(permissionsData)) {
        const user = usersWithPermissions.find(u => u.id === parseInt(userId));
        if (user) {
          // Porównaj z oryginalnymi uprawnieniami użytkownika
          let originalPerms = user.permissions || {};
          // Upewnij się, że originalPerms jest obiektem
          if (typeof originalPerms === 'string') {
            try {
              originalPerms = JSON.parse(originalPerms);
            } catch (e) {
              originalPerms = {};
            }
          }
          
          console.log(`🔍 [handleSaveAll] Porównywanie dla użytkownika ${userId} (${user.name}):`);
          console.log(`🔍 [handleSaveAll] Oryginalne uprawnienia:`, JSON.stringify(originalPerms, null, 2));
          console.log(`🔍 [handleSaveAll] Nowe uprawnienia (z permissionsData):`, JSON.stringify(permissions, null, 2));
          
          // Normalizuj oba obiekty przed porównaniem (usuń undefined/null wartości)
          const normalize = (obj) => {
            if (typeof obj !== 'object' || obj === null) return {};
            const normalized = {};
            for (const [key, value] of Object.entries(obj)) {
              if (value !== null && value !== undefined) {
                if (typeof value === 'object' && !Array.isArray(value)) {
                  const nested = normalize(value);
                  if (Object.keys(nested).length > 0) {
                    normalized[key] = nested;
                  }
                } else {
                  normalized[key] = value;
                }
              }
            }
            return normalized;
          };
          
          const normalizedOriginal = normalize(originalPerms);
          const normalizedNew = normalize(permissions);
          
          console.log(`🔍 [handleSaveAll] Znormalizowane oryginalne:`, JSON.stringify(normalizedOriginal, null, 2));
          console.log(`🔍 [handleSaveAll] Znormalizowane nowe:`, JSON.stringify(normalizedNew, null, 2));
          
          const originalStr = JSON.stringify(normalizedOriginal);
          const newStr = JSON.stringify(normalizedNew);
          const hasChanges = originalStr !== newStr;
          
          console.log(`🔍 [handleSaveAll] Porównanie stringów: "${originalStr}" !== "${newStr}" = ${hasChanges}`);
          
          if (hasChanges) {
            console.log(`💾 Zapisywanie uprawnień dla użytkownika ${userId} (${user.name}):`, permissions);
            try {
              const result = await permissionsAPI.updateUserPermissions(parseInt(userId), permissions);
              console.log(`✅ Zapisano uprawnienia dla użytkownika ${userId}, odpowiedź z serwera:`, result);
              saveResults.push({ userId: parseInt(userId), success: true, result });
            } catch (err) {
              console.error(`❌ Błąd zapisywania uprawnień dla użytkownika ${userId}:`, err);
              saveResults.push({ userId: parseInt(userId), success: false, error: err });
            }
          } else {
            console.log(`⏭️ [handleSaveAll] Brak zmian dla użytkownika ${userId} (${user.name})`);
            saveResults.push({ userId: parseInt(userId), success: false, reason: 'no changes' });
          }
        }
      }
      
      // Sprawdź wyniki zapisywania
      const successful = saveResults.filter(r => r.success).length;
      const failed = saveResults.filter(r => !r.success && r.error).length;
      const noChanges = saveResults.filter(r => !r.success && r.reason === 'no changes').length;
      
      console.log(`📊 Wyniki zapisywania: ${successful} zapisanych, ${failed} błędów, ${noChanges} bez zmian`);
      
      if (failed > 0) {
        setError(`Nie udało się zapisać uprawnień dla ${failed} użytkowników`);
      } else {
        setSuccess('Wszystkie uprawnienia zostały zapisane pomyślnie!');
      }
      
      setHasUnsavedChanges(false);
      
      // Przeładuj dane, aby mieć aktualne uprawnienia
      // Dodaj małe opóźnienie, aby upewnić się, że backend zaktualizował dane
      console.log('🔄 Przeładowywanie danych po zapisaniu...');
      await new Promise(resolve => setTimeout(resolve, 500));
      await loadData();
      console.log('✅ Dane przeładowane po zapisaniu');
      
      // NIE wywołuj onUpdate() tutaj, ponieważ loadAllData() w App.js
      // ładuje użytkowników bez uprawnień i może nadpisać nasze dane
      // if (onUpdate) {
      //   onUpdate();
      // }
    } catch (err) {
      console.error('Błąd zapisywania uprawnień:', err);
      setError(err.message || 'Błąd zapisywania uprawnień');
    } finally {
      setLoading(false);
    }
  };

  // Funkcja do anulowania zmian
  const handleCancel = () => {
    if (window.confirm('Czy na pewno chcesz anulować wszystkie niezapisane zmiany?')) {
      loadData(); // Przeładuj oryginalne dane
      setHasUnsavedChanges(false);
      setError('');
      setSuccess('');
    }
  };

  const getRoleDisplayName = (role) => {
    const roleNames = {
      'owner': 'Owner',
      'admin': 'Admin',
      'lider': 'Lider',
      'user': 'Użytkownik',
      'guest': 'Gość',
      'employee': 'Pracownik'
    };
    return roleNames[role] || role;
  };

  // Grupowanie uprawnień według kategorii
  const groupedPermissions = useMemo(() => {
    const groups = {};
    filteredPermissions.forEach(perm => {
      if (!groups[perm.category]) {
        groups[perm.category] = [];
      }
      groups[perm.category].push(perm);
    });
    return groups;
  }, [filteredPermissions]);

  if (loading && !systemFunctions) {
    return <div className="permissions-grid-loading">Ładowanie...</div>;
  }

  return (
    <div className="permissions-grid-container">
      <div className="permissions-grid-header">
        <h2>Przegląd uprawnień użytkowników</h2>
      </div>

      {error && <div className="permissions-grid-message error">{error}</div>}
      {success && <div className="permissions-grid-message success">{success}</div>}

      {/* Pasek narzędzi */}
      <div className="permissions-grid-toolbar">
        <div className="toolbar-left">
          <input
            type="text"
            placeholder="Szukaj użytkownika lub uprawnienia..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <span className="owner-info-text-inline">
            Użytkownicy z rolą Owner mają nieograniczony dostęp do wszystkich funkcji
          </span>
        </div>
        <div className="toolbar-right">
          {hasUnsavedChanges && (
            <span className="unsaved-indicator">● Niezapisane zmiany</span>
          )}
          <button
            onClick={handleCancel}
            disabled={!hasUnsavedChanges || loading}
            className="cancel-button"
          >
            Anuluj
          </button>
          <button
            onClick={handleSaveAll}
            disabled={!hasUnsavedChanges || loading}
            className="save-button"
          >
            {loading ? 'Zapisywanie...' : 'Zapisz wszystkie zmiany'}
          </button>
        </div>
      </div>

      {/* Grid */}
      <div className="permissions-grid-wrapper">
        <div className="permissions-grid-table">
          {/* Header z użytkownikami */}
          <div className="permissions-grid-header-row">
            <div className="permissions-grid-header-cell permissions-grid-header-cell-fixed">
              <div className="header-cell-content">
                <div className="header-cell-label">Dział</div>
                <div className="header-cell-label-secondary">Uprawnienie</div>
              </div>
            </div>
            {filteredUsers.map((user) => (
              <div
                key={user.id}
                className={`permissions-grid-header-cell ${selectedUserColumn === user.id ? 'selected' : ''}`}
                onMouseEnter={() => setSelectedUserColumn(user.id)}
                onMouseLeave={() => setSelectedUserColumn(null)}
              >
                <div className="header-cell-content">
                  <div className="header-user-name">{user.name || user.email}</div>
                  <div className="header-user-role">{getRoleDisplayName(user.role)}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Wiersze z uprawnieniami */}
          {Object.entries(groupedPermissions).map(([category, perms]) => (
            <React.Fragment key={category}>
              {/* Nagłówek kategorii */}
              <div className="permissions-grid-category-row">
                <div className="permissions-grid-category-cell permissions-grid-header-cell-fixed">
                  <div className="category-label">{category}</div>
                </div>
                {filteredUsers.map((user) => (
                  <div
                    key={user.id}
                    className={`permissions-grid-category-cell ${selectedUserColumn === user.id ? 'selected' : ''}`}
                    onMouseEnter={() => setSelectedUserColumn(user.id)}
                    onMouseLeave={() => setSelectedUserColumn(null)}
                  />
                ))}
              </div>

              {/* Uprawnienia w kategorii */}
              {perms.map((permission) => (
                <div key={permission.fullPath} className="permissions-grid-data-row">
                  <div className="permissions-grid-data-cell permissions-grid-header-cell-fixed">
                    <div className="permission-label-cell">
                      <div className="permission-label">{permission.label}</div>
                      {permission.description && (
                        <div className="permission-description" title={permission.description}>
                          {permission.description}
                        </div>
                      )}
                    </div>
                  </div>
                  {filteredUsers.map((user) => {
                    const isChecked = getPermissionValue(user.id, permission.fullPath);
                    return (
                      <div
                        key={user.id}
                        className={`permissions-grid-data-cell ${selectedUserColumn === user.id ? 'selected' : ''}`}
                        onMouseEnter={() => setSelectedUserColumn(user.id)}
                        onMouseLeave={() => setSelectedUserColumn(null)}
                      >
                        <label className="permission-checkbox-cell">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => handlePermissionChange(user.id, permission.fullPath, e.target.checked)}
                            className="permission-checkbox"
                          />
                          <span className="checkbox-custom"></span>
                        </label>
                      </div>
                    );
                  })}
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Informacja o liczbie użytkowników i uprawnień */}
      <div className="permissions-grid-footer">
        <div className="footer-info">
          <span>Użytkownicy: {filteredUsers.length}</span>
          <span>Uprawnienia: {filteredPermissions.length}</span>
        </div>
      </div>
    </div>
  );
}

export default PermissionsGrid;
